import { Link, usePage, router } from "@inertiajs/react"
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import { Toaster } from "@/components/ui/sonner"
import FlashMessages from "@/components/flash-messages"
import GoogleOneTap from "@/components/auth/google-one-tap"
import PlatformIcon from "@/components/dashboard/platform-icon"

const NAV_LINKS = [
  { href: "/how-it-works", label: "How it works" },
  { href: "/features", label: "Features" },
  { href: "/examples", label: "Examples" },
  { href: "/pricing", label: "Pricing" },
] as const

const SECONDARY_LINKS = [
  { href: "/about", label: "About" },
  { href: "/faq", label: "FAQ" },
  { href: "/contact", label: "Contact" },
] as const

const SOCIAL_LINKS = [
  { href: "https://www.instagram.com/quiterightuk/", platform: "instagram", label: "Instagram" },
  { href: "https://www.facebook.com/profile.php?id=61579489521742", platform: "facebook", label: "Facebook" },
  { href: "https://www.youtube.com/@quiterightapp", platform: "youtube", label: "YouTube" },
  { href: "https://www.tiktok.com/@quiterightuk", platform: "tiktok", label: "TikTok" },
] as const

function Logo({ variant = "dark" }: { variant?: "dark" | "light" }) {
  return (
    <Link
      href="/"
      className="flex items-baseline gap-0 text-2xl font-display font-semibold tracking-tight"
    >
      <span className={variant === "dark" ? "text-charcoal" : "text-cream"}>
        Quite
      </span>
      <span className="text-red">Right.</span>
    </Link>
  )
}

function MenuIcon({ open }: { open: boolean }) {
  return (
    <div className="relative size-6" aria-hidden="true">
      <span
        className={`absolute left-0 block h-[1.5px] w-6 bg-current transition-all duration-300 ease-out ${
          open
            ? "top-[11px] rotate-45"
            : "top-[5px] rotate-0"
        }`}
      />
      <span
        className={`absolute left-0 top-[11px] block h-[1.5px] w-6 bg-current transition-all duration-300 ease-out ${
          open ? "opacity-0 scale-x-0" : "opacity-100 scale-x-100"
        }`}
      />
      <span
        className={`absolute left-0 block h-[1.5px] w-6 bg-current transition-all duration-300 ease-out ${
          open
            ? "top-[11px] -rotate-45"
            : "top-[17px] rotate-0"
        }`}
      />
    </div>
  )
}

function MobileMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { url } = usePage()
  const menuRef = useRef<HTMLDivElement>(null)

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [open])

  // Close on escape
  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") onClose()
    }
    if (open) {
      document.addEventListener("keydown", handleEscape)
      return () => document.removeEventListener("keydown", handleEscape)
    }
  }, [open, onClose])

  // Trap focus within menu when open
  useEffect(() => {
    if (!open || !menuRef.current) return

    const focusable = menuRef.current.querySelectorAll<HTMLElement>(
      'a[href], button, [tabindex]:not([tabindex="-1"])'
    )
    if (focusable.length > 0) {
      focusable[0].focus()
    }
  }, [open])

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-charcoal/30 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={menuRef}
        role="dialog"
        aria-modal={open ? true : undefined}
        aria-label="Navigation menu"
        aria-hidden={!open}
        inert={!open}
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-charcoal transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] lg:hidden ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Panel header */}
        <div className="flex items-center justify-between px-6 py-4">
          <Logo variant="light" />
          <button
            onClick={onClose}
            className="flex size-10 items-center justify-center rounded-full text-cream transition-colors hover:bg-slate/40"
            aria-label="Close menu"
          >
            <MenuIcon open={true} />
          </button>
        </div>

        {/* Decorative red line */}
        <div className="mx-6 h-px bg-gradient-to-r from-red via-red/40 to-transparent" />

        {/* Navigation */}
        <nav className="flex flex-col px-6 pt-8">
          <div className="space-y-1">
            {NAV_LINKS.map((link, i) => {
              const active = url.startsWith(link.href)
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={onClose}
                  className={`group flex items-center gap-4 rounded-lg px-3 py-3.5 transition-all duration-300 ${
                    open
                      ? "translate-x-0 opacity-100"
                      : "translate-x-8 opacity-0"
                  } ${active ? "bg-slate/30" : "hover:bg-slate/20"}`}
                  style={{
                    transitionDelay: open ? `${150 + i * 60}ms` : "0ms",
                  }}
                >
                  <span
                    className={`h-5 w-0.5 rounded-full transition-colors ${
                      active ? "bg-red" : "bg-warm-grey/30 group-hover:bg-red/50"
                    }`}
                  />
                  <span
                    className={`font-display text-2xl font-medium tracking-tight ${
                      active ? "text-cream" : "text-stone/80 group-hover:text-cream"
                    }`}
                  >
                    {link.label}
                  </span>
                </Link>
              )
            })}
          </div>

          {/* Divider */}
          <div className="mx-3 my-5 h-px bg-slate/30" />

          {/* Secondary links */}
          <div className="space-y-0.5">
            {SECONDARY_LINKS.map((link, i) => {
              const active = url.startsWith(link.href)
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={onClose}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-300 ${
                    open
                      ? "translate-x-0 opacity-100"
                      : "translate-x-8 opacity-0"
                  } ${
                    active
                      ? "text-cream"
                      : "text-warm-grey transition-colors hover:text-stone"
                  }`}
                  style={{
                    transitionDelay: open
                      ? `${150 + (NAV_LINKS.length + i) * 60}ms`
                      : "0ms",
                  }}
                >
                  {link.label}
                </Link>
              )
            })}
          </div>
        </nav>

        {/* Bottom CTA area */}
        <div
          className={`absolute inset-x-0 bottom-0 border-t border-slate/20 px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-6 transition-all duration-500 ${
            open
              ? "translate-y-0 opacity-100"
              : "translate-y-4 opacity-0"
          }`}
          style={{ transitionDelay: open ? "450ms" : "0ms" }}
        >
          <Link
            href="/sign-up"
            onClick={onClose}
            className="flex w-full items-center justify-center rounded-lg bg-red px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red"
          >
            Sign up
          </Link>
          <Link
            href="/sign-in"
            onClick={onClose}
            className="mt-3 flex w-full items-center justify-center rounded-lg border border-slate/30 px-6 py-3 text-sm font-medium text-stone transition-colors hover:border-stone/40 hover:text-cream"
          >
            Sign in
          </Link>
        </div>
      </div>
    </>
  )
}

interface PublicLayoutProps {
  children: ReactNode
  /**
   * When true, the header starts fully transparent over a dark cinematic
   * hero, then transitions through three states as the user scrolls:
   *
   *   1. "top"      — at the very top: bg-transparent, cream text.
   *   2. "on-hero"  — scrolled but still over the dark hero: charcoal/70
   *                   backdrop-blur with cream text. Header "pops" into
   *                   existence as a dark glass panel.
   *   3. "past-hero"— scrolled past the hero onto cream content: standard
   *                   cream/80 backdrop-blur with dark text.
   *
   * The hero's bottom edge is detected via a `data-public-hero` element on
   * the page (falls back to one viewport height). Used by the homepage and
   * any future page opening with a charcoal hero.
   */
  transparentOverHero?: boolean
}

type HeaderScrollState = "top" | "on-hero" | "past-hero"

const HEADER_HEIGHT_PX = 68
const TOP_THRESHOLD_PX = 24

export default function PublicLayout({ children, transparentOverHero = false }: PublicLayoutProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  // Raw scroll state, only meaningful when transparentOverHero is true.
  // For pages without a hero overlay we ignore this and use a constant
  // "past-hero" via the derived `scrollState` below — that keeps the
  // setState-only-on-real-changes contract clean.
  const [transparentScrollState, setTransparentScrollState] = useState<HeaderScrollState>("top")
  const { url } = usePage()

  const closeMenu = useCallback(() => setMenuOpen(false), [])

  // Close menu on Inertia navigation
  useEffect(() => {
    return router.on("navigate", closeMenu)
  }, [closeMenu])

  // Watch scroll position and step the header through its three states.
  // Only mounts the listener when transparentOverHero is set — otherwise
  // the header is permanently in the "past-hero" (solid cream) state via
  // the derived value below.
  useEffect(() => {
    if (!transparentOverHero) return

    // Cache the hero element ref but always read its current rect — the
    // hero's height can change as fonts/images load, so we can't snapshot
    // its bottom once at mount time.
    let heroEl: HTMLElement | null = document.querySelector("[data-public-hero]")

    const computePopOutAt = () => {
      if (!heroEl || !heroEl.isConnected) {
        heroEl = document.querySelector("[data-public-hero]")
      }
      if (heroEl) {
        const rect = heroEl.getBoundingClientRect()
        return rect.bottom + window.scrollY - HEADER_HEIGHT_PX
      }
      // Fallback for pages that don't mark a hero: assume one viewport.
      return window.innerHeight - HEADER_HEIGHT_PX
    }

    const onScroll = () => {
      const y = window.scrollY
      // Switch to "past-hero" the moment the hero's bottom edge crosses the
      // bottom of the (fixed) header — that's when the cream content first
      // touches the header.
      const popOutAt = computePopOutAt()
      if (y < TOP_THRESHOLD_PX) {
        setTransparentScrollState("top")
      } else if (y < popOutAt) {
        setTransparentScrollState("on-hero")
      } else {
        setTransparentScrollState("past-hero")
      }
    }

    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
    }
  }, [transparentOverHero])

  // Pages without the hero overlay are always in the solid cream state.
  const scrollState: HeaderScrollState = transparentOverHero
    ? transparentScrollState
    : "past-hero"
  // When the menu is open we force the solid cream state so the hamburger
  // icon and backdrop have a stable visual anchor.
  const effectiveState: HeaderScrollState = menuOpen ? "past-hero" : scrollState
  const isOverHero = effectiveState !== "past-hero"

  // Positioning: pages that open with a dark hero use a fixed header so the
  // hero can sit *behind* the (initially transparent) header. Other pages
  // keep the standard sticky-in-flow header.
  const headerPositionClass = transparentOverHero
    ? "fixed top-0 inset-x-0"
    : "sticky top-0"

  // Header background per state
  let headerBgClass: string
  if (effectiveState === "top") {
    headerBgClass = "border-b border-transparent bg-transparent"
  } else if (effectiveState === "on-hero") {
    headerBgClass = "border-b border-slate/30 bg-charcoal/70 backdrop-blur-lg"
  } else {
    headerBgClass = "border-b border-stone/80 bg-cream/80 backdrop-blur-lg"
  }

  // Text colours: cream while over the hero (states 1 + 2), slate/charcoal
  // once we're past it.
  const navLinkBaseClass = isOverHero
    ? "text-cream/75 hover:text-cream"
    : "text-slate hover:text-charcoal"
  const navLinkActiveClass = isOverHero ? "text-cream" : "text-charcoal"
  const signInClass = isOverHero
    ? "text-cream/85 hover:text-cream"
    : "text-slate hover:text-charcoal"
  const menuButtonClass = isOverHero
    ? "text-cream hover:bg-cream/10"
    : "text-charcoal hover:bg-stone/60"

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header
        className={`z-30 px-6 transition-all duration-500 ease-out ${headerPositionClass} ${headerBgClass}`}
      >
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between lg:h-[4.25rem]">
          <div className="flex items-center gap-8">
          <Logo variant={isOverHero ? "light" : "dark"} />

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
            {NAV_LINKS.map((link) => {
              const active = url.startsWith(link.href)
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`relative rounded-md px-3.5 py-2 text-sm font-medium transition-colors duration-500 ${
                    active ? navLinkActiveClass : navLinkBaseClass
                  }`}
                >
                  {link.label}
                  {active && (
                    <span className="absolute inset-x-3.5 -bottom-[1.19rem] h-[2px] rounded-full bg-red" />
                  )}
                </Link>
              )
            })}
          </nav>
          </div>

          {/* Desktop right side */}
          <div className="hidden items-center gap-3 lg:flex">
            <Link
              href="/sign-in"
              className={`rounded-md px-3.5 py-2 text-sm font-medium transition-colors duration-500 ${signInClass}`}
            >
              Sign in
            </Link>
            <Link
              href="/sign-up"
              className="inline-flex items-center rounded-lg bg-red px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-deep-red hover:shadow-md hover:shadow-red/10"
            >
              Sign up
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMenuOpen((prev) => !prev)}
            className={`flex size-10 items-center justify-center rounded-full transition-colors duration-500 lg:hidden ${menuButtonClass}`}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
          >
            <MenuIcon open={menuOpen} />
          </button>
        </div>
      </header>

      <MobileMenu open={menuOpen} onClose={closeMenu} />

      <GoogleOneTap />
      <FlashMessages />
      <Toaster position="top-right" richColors />
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-charcoal px-5 py-12 text-stone sm:px-6 sm:py-16">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
            {/* Brand */}
            <div className="sm:col-span-2 lg:col-span-1">
              <span className="text-xl font-display font-semibold">
                <span className="text-cream">Quite</span>
                <span className="text-red">Right.</span>
              </span>
              <p className="mt-3 max-w-xs text-sm leading-relaxed text-warm-grey">
                Scroll-stopping property video ads for estate agents. Paste a
                listing, get a video.
              </p>
            </div>

            {/* Product */}
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
                Product
              </p>
              <ul className="mt-4 space-y-2.5 text-sm">
                <li>
                  <Link
                    href="/how-it-works"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    How it works
                  </Link>
                </li>
                <li>
                  <Link
                    href="/features"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Features
                  </Link>
                </li>
                <li>
                  <Link
                    href="/examples"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Examples
                  </Link>
                </li>
                <li>
                  <Link
                    href="/pricing"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Pricing
                  </Link>
                </li>
                <li>
                  <Link
                    href="/why-quiteright"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Why QuiteRight
                  </Link>
                </li>
                <li>
                  <Link
                    href="/integrations"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Integrations
                  </Link>
                </li>
              </ul>
            </div>

            {/* Solutions */}
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
                Solutions
              </p>
              <ul className="mt-4 space-y-2.5 text-sm">
                <li>
                  <Link
                    href="/for/independent-agents"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Independent agents
                  </Link>
                </li>
                <li>
                  <Link
                    href="/for/letting-agents"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Letting agents
                  </Link>
                </li>
                <li>
                  <Link
                    href="/for/multi-branch"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Multi-branch agencies
                  </Link>
                </li>
                <li>
                  <Link
                    href="/for/holiday-lets"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Holiday lets
                  </Link>
                </li>
                <li>
                  <Link
                    href="/for/btl-landlords"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    BTL landlords
                  </Link>
                </li>
                <li>
                  <Link
                    href="/for/new-homes-developers"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    New homes developers
                  </Link>
                </li>
                <li>
                  <Link
                    href="/for/commercial-agents"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Commercial agents
                  </Link>
                </li>
                <li>
                  <Link
                    href="/for/scottish-agents"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Scottish agents
                  </Link>
                </li>
                <li>
                  <Link
                    href="/use-cases"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Use cases
                  </Link>
                </li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
                Resources
              </p>
              <ul className="mt-4 space-y-2.5 text-sm">
                <li>
                  <Link
                    href="/guides"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Guides
                  </Link>
                </li>
                <li>
                  <Link
                    href="/tools"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Tools &amp; calculators
                  </Link>
                </li>
                <li>
                  <Link
                    href="/glossary"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Glossary
                  </Link>
                </li>
                <li>
                  <Link
                    href="/directory"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Agent directory
                  </Link>
                </li>
                <li>
                  <Link
                    href="/best"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Best estate agents
                  </Link>
                </li>
                <li>
                  <Link
                    href="/areas"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Area guides
                  </Link>
                </li>
                <li>
                  <Link
                    href="/insights"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Local market insights
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
                Company
              </p>
              <ul className="mt-4 space-y-2.5 text-sm">
                <li>
                  <Link
                    href="/about"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    About
                  </Link>
                </li>
                <li>
                  <Link
                    href="/faq"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link
                    href="/contact"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Contact
                  </Link>
                </li>
                <li>
                  <Link
                    href="/sign-up"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Get started
                  </Link>
                </li>
                <li>
                  <Link
                    href="/demo"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Request a demo
                  </Link>
                </li>
                <li>
                  <Link
                    href="/privacy"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Privacy policy
                  </Link>
                </li>
                <li>
                  <Link
                    href="/terms"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Terms of service
                  </Link>
                </li>
                <li>
                  <Link
                    href="/sitemap"
                    className="text-stone/70 transition-colors hover:text-cream"
                  >
                    Sitemap
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Social links */}
          <div className="mt-10 flex items-center gap-4 sm:mt-12">
            {SOCIAL_LINKS.map((link) => (
              <a
                key={link.platform}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={link.label}
                className="flex size-9 items-center justify-center rounded-full bg-slate/30 text-cream transition-colors hover:bg-slate/60"
              >
                <PlatformIcon platform={link.platform} className="size-4" />
              </a>
            ))}
          </div>

          <div className="mt-6 border-t border-slate pt-6 flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p className="text-xs text-warm-grey">
              &copy; {new Date().getFullYear()} QuiteRight. All rights reserved.
            </p>
            <p className="text-xs text-warm-grey">
              Your property, presented properly.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
